import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PerforatedDivider from '../components/PerforatedDivider';
import { SECTORS } from '../data/sectors';
import { ArrowRight } from 'lucide-react';

/**
 * Hub del clúster de sectores (/papel-para).
 *
 * Enlaza a las cinco páginas de sector y ellas enlazan de vuelta aquí:
 * es la mitad "hub" del hub-and-spoke, lo que evita que las páginas nuevas
 * queden huérfanas colgando solo del bloque de Home.
 */
export default function Sectores() {
    return (
        <div data-testid="sectores-page">
            <Seo
                title="Papel Grado Alimenticio por Tipo de Negocio | Carpepel"
                description="Papel grado alimenticio para restaurantes, panaderías, cafeterías, pizzerías y taquerías: qué gramaje conviene a cada negocio. Mayoreo y menudeo en México."
                keywords={[
                    'papel grado alimenticio por tipo de negocio',
                    'papel para negocios de comida',
                    'proveedor de papel para restaurantes',
                    'papel para panaderia',
                    'papel para pizzeria',
                    'papel para cafeteria',
                    'papel para taqueria',
                ]}
                canonicalPath="/papel-para"
                schema={[
                    {
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://www.carpepel.com' },
                            { '@type': 'ListItem', position: 2, name: 'Por tipo de negocio', item: 'https://www.carpepel.com/papel-para' },
                        ],
                    },
                    {
                        '@type': 'ItemList',
                        name: 'Papel grado alimenticio por tipo de negocio',
                        itemListElement: SECTORS.map((s, i) => ({
                            '@type': 'ListItem',
                            position: i + 1,
                            name: s.h1,
                            url: `https://www.carpepel.com/papel-para/${s.slug}`,
                        })),
                    },
                ]}
            />

            <div style={{ backgroundColor: 'var(--ink)' }} className="w-full py-8">
                <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center gap-5">
                    <div
                        className="hidden md:block w-[3px] self-stretch rounded-full"
                        style={{ backgroundColor: 'var(--kraft-deep)', minHeight: '48px' }}
                    />
                    <div>
                        <p className="text-[11px] font-bold uppercase tracking-widest mb-0.5" style={{ color: 'var(--ink-dim)' }}>
                            Papel Grado Alimenticio
                        </p>
                        <h1 className="font-heading text-2xl sm:text-3xl font-black text-white leading-none">
                            POR TIPO DE <span style={{ color: 'var(--kraft-deep)' }}>NEGOCIO</span>
                        </h1>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
                <p className="text-base text-[var(--ink-soft)] leading-relaxed max-w-2xl mb-10">
                    El catálogo es el mismo; lo que cambia es el gramaje y la presentación que
                    conviene a cada negocio. Un papel de 18 gramos es correcto para pan y equivocado
                    para fritura, y al revés.
                </p>

                <div className="border-t border-[var(--line)]">
                    {SECTORS.map((sector, i) => (
                        <Link
                            key={sector.slug}
                            to={`/papel-para/${sector.slug}`}
                            data-testid={`sector-row-${sector.slug}`}
                            className="reveal-on-scroll group grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-6 items-baseline py-6 border-b border-[var(--line)] transition-colors duration-300"
                        >
                            <div className="md:col-span-1 font-mono text-[11px] text-[var(--ink-dim)]">
                                {String(i + 1).padStart(2, '0')}
                            </div>
                            <div className="md:col-span-4 flex items-center gap-3">
                                <sector.Icon className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--kraft-deep)' }} />
                                <h2 className="font-heading text-lg font-bold text-[var(--ink)] leading-snug group-hover:text-[var(--kraft-deep)] transition-colors duration-300">
                                    {sector.label}
                                </h2>
                            </div>
                            <p className="md:col-span-6 text-sm text-[var(--ink-soft)] leading-relaxed">
                                {sector.hook}
                            </p>
                            <div className="md:col-span-1 flex md:justify-end">
                                <ArrowRight className="w-4 h-4 text-[var(--ink-dim)] transition-all duration-300 group-hover:text-[var(--kraft-deep)] group-hover:translate-x-1" />
                            </div>
                        </Link>
                    ))}
                </div>

                <PerforatedDivider className="my-12" />

                <div className="max-w-2xl">
                    <h2 className="font-heading text-base font-bold uppercase tracking-wide text-[var(--ink)] mb-3">
                        ¿No encuentras tu giro?
                    </h2>
                    <p className="text-sm text-[var(--ink-soft)] leading-relaxed mb-5">
                        La clasificación de arriba es por gramaje, no por giro: si tu producto es
                        seco y ligero, te sirve lo mismo que a una panadería; si es graso, lo mismo
                        que a un restaurante. La guía de gramajes lo explica en detalle.
                    </p>
                    <Link
                        to="/guias/como-elegir-el-gramaje-correcto"
                        className="inline-flex items-center gap-2 text-sm font-bold uppercase tracking-widest transition-colors duration-200"
                        style={{ color: 'var(--kraft-deep)' }}
                    >
                        Cómo elegir el gramaje <ArrowRight className="w-3.5 h-3.5" />
                    </Link>
                </div>
            </div>
        </div>
    );
}
